/**
 * ExtranetSignup — Fase 4 Extranet KB
 * Auto-cadastro (quando tenant.extranet_signup_enabled=true).
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ExtranetAuthProvider, useExtranetAuth } from '@/hooks/useExtranetAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Loader2, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { resolveAuthError, type AuthErrorInfo } from '@/lib/extranet/authErrors';
import { ExtranetAuthAlert } from '@/components/kb/extranet/ExtranetAuthAlert';

function SignupInner() {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const navigate = useNavigate();
  const { signUp } = useExtranetAuth();
  const [form, setForm] = useState({
    display_name: '',
    email: '',
    organization: '',
    password: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [tenantName, setTenantName] = useState<string>('Portal do Cliente');
  const [alertInfo, setAlertInfo] = useState<AuthErrorInfo | null>(null);


  useEffect(() => {
    if (!tenantSlug) return;
    (async () => {
      // Igualdade exata (evita metacaracteres do ilike como % ou _)
      const { data } = await supabase
        .from('tenants')
        .select('name, extranet_signup_enabled')
        .eq('slug', tenantSlug.trim().toLowerCase())
        .maybeSingle();
      setEnabled(!!data?.extranet_signup_enabled);
      if (data?.name) setTenantName(data.name);
    })();
  }, [tenantSlug]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAlertInfo(null);
    if (form.password.length < 8) {
      setAlertInfo(resolveAuthError({ code: 'weak_password' }));
      return;
    }
    setSubmitting(true);
    const result = await signUp({
      display_name: form.display_name.trim(),
      email: form.email.trim(),
      organization: form.organization.trim() || undefined,
      password: form.password,
    });
    setSubmitting(false);
    if (!result.success) {
      setAlertInfo(
        resolveAuthError({
          backendCode: result.backendCode,
          code: result.code,
          status: result.status,
          message: result.error ?? result.message,
        }),
      );
      if (result.redirect_to) navigate(result.redirect_to);
      return;
    }
    toast.success(result.message || 'Cadastro recebido!');
    navigate(result.redirect_to || `/extranet/${tenantSlug}/confirme-email`);
  };

  if (enabled === false) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-6 text-center space-y-3">
          <h1 className="text-lg font-semibold">Auto-cadastro indisponível</h1>
          <p className="text-sm text-muted-foreground">
            Esta organização não aceita cadastro público. Solicite um convite ao administrador.
          </p>
          <Button asChild variant="outline">
            <Link to={`/extranet/${tenantSlug}/login`}>Voltar ao login</Link>
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
      <Helmet>
        <title>Cadastro — {tenantName}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Card className="w-full max-w-md p-6 space-y-5">
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="h-12 w-12 rounded bg-primary/10 flex items-center justify-center">
            <BookOpen className="h-6 w-6 text-primary" />
          </div>
          <h1 className="text-xl font-semibold">Solicitar acesso</h1>
          <p className="text-sm text-muted-foreground">{tenantName}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <ExtranetAuthAlert info={alertInfo} hrefPrefix={`/extranet/${tenantSlug}`} />
          <div className="space-y-1.5">
            <Label htmlFor="display_name">Nome completo</Label>
            <Input
              id="display_name"
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              required
              maxLength={120}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">E-mail</Label>
            <Input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              maxLength={255}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="organization">Empresa (opcional)</Label>
            <Input
              id="organization"
              value={form.organization}
              onChange={(e) => setForm({ ...form, organization: e.target.value })}
              maxLength={120}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Senha</Label>
            <Input
              id="password"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              minLength={8}
              maxLength={200}
            />
          </div>
          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Solicitar cadastro
          </Button>
        </form>

        <div className="text-center text-sm">
          <Link to={`/extranet/${tenantSlug}/login`} className="text-muted-foreground hover:text-foreground">
            Já tenho conta — entrar
          </Link>
        </div>
      </Card>
    </div>
  );
}

export default function ExtranetSignup() {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  return (
    <ExtranetAuthProvider tenantSlug={tenantSlug}>
      <SignupInner />
    </ExtranetAuthProvider>
  );
}
